/* eslint-disable no-unused-vars */
import { isDevelopment } from '@/common/utils';
import { useState, useEffect, useCallback, useRef } from 'react';
// 광고 상태를 나타내는 Enum
export enum AdStatus {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  CREATED = 'CREATED', // 새로 추가: 광고 슬롯이 생성되고 표시됨
  READY = 'READY', // 광고가 완전히 로드되고 표시 준비됨
  SHOWING = 'SHOWING',
  CLOSED = 'CLOSED',
  REWARDED = 'REWARDED',
  ERROR = 'ERROR',
}
declare global {
  interface Window {
    googletag: any;
  }
}
// 테스트용: '/22639388115/rewarded_web_example'
// 운영용: '/22802458718/start_chapter_reward'
export const AD_UNIT_ID = isDevelopment()
  ? '/22639388115/rewarded_web_example'
  : '/22802458718/start_chapter_reward';

const COOLDOWN_TIME = 5; // 10 seconds cooldown
const useRewardedAd = (
  autoRequestEnabled = true,
  grantCallback?: (payload: { amount: number; type: string }) => void,
) => {
  const [status, setStatus] = useState('');
  const [isAdReady, setIsAdReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rewardPayload, setRewardPayload] = useState<{
    amount: number;
    type: string;
  } | null>(null);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [isAutoRequestEnabled, setIsAutoRequestEnabled] =
    useState(autoRequestEnabled);
  const rewardedSlotRef = useRef<any>(null);
  const makeRewardedVisibleRef = useRef<(() => void) | null>(null);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRewardGrantedRef = useRef(false);
  const retryCount = useRef(0);

  const loadAd = useCallback(() => {
    if (status === AdStatus.LOADING) return;
    setIsLoading(true);
    setStatus(AdStatus.LOADING);

    window.googletag.cmd.push(() => {
      if (rewardedSlotRef.current) {
        window.googletag.destroySlots([rewardedSlotRef.current]);
      }

      const slot = window.googletag.defineOutOfPageSlot(
        AD_UNIT_ID,
        window.googletag.enums.OutOfPageFormat.REWARDED,
      );

      if (slot) {
        slot.addService(window.googletag.pubads());
        rewardedSlotRef.current = slot;
        window.googletag.display(slot);
        // console.log('New ad slot created and displayed');
        setStatus(AdStatus.CREATED);
      } else {
        // console.log('Failed to create new ad slot');
        setStatus(AdStatus.ERROR);
        setIsLoading(false);
      }
    });
  }, []);

  const startCooldownTimer = useCallback(() => {
    setCooldownTime(COOLDOWN_TIME);
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    cooldownTimerRef.current = setInterval(() => {
      setCooldownTime((prevTime) => {
        if (prevTime <= 1) {
          if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
          }
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
    script.async = true;
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);

    script.onload = () => {
      window.googletag = window.googletag || { cmd: [] };
      window.googletag.cmd.push(() => {
        // Enable SRA and services.
        // window.googletag.pubads().enableSingleRequest();

        window.googletag
          .pubads()
          .addEventListener('rewardedSlotReady', (event: any) => {
            // console.log('Rewarded slot ready event fired');
            retryCount.current = 0;
            setStatus(AdStatus.READY);
            setIsAdReady(true);
            setIsLoading(false);
            makeRewardedVisibleRef.current = event.makeRewardedVisible;
          });

        window.googletag.pubads().addEventListener('rewardedSlotClosed', () => {
          // console.log('Rewarded slot closed event fired');
          setStatus(AdStatus.CLOSED);
          setIsAdReady(false);
          setRewardPayload(null);
          isRewardGrantedRef.current = false;
          startCooldownTimer();
        });

        window.googletag
          .pubads()
          .addEventListener('rewardedSlotGranted', (event: any) => {
            // console.log('Rewarded slot granted event fired');
            if (!isRewardGrantedRef.current) {
              isRewardGrantedRef.current = true;
              setRewardPayload(event.payload);
              setStatus(AdStatus.REWARDED);
              grantCallback?.(event.payload);
              // console.log('Reward granted');
            } else {
              console.warn(
                'Duplicate rewardedSlotGranted event detected and ignored',
              );
            }
          });

        window.googletag
          .pubads()
          .addEventListener('slotRenderEnded', (event: any) => {
            if (event.slot === rewardedSlotRef.current && event.isEmpty) {
              // setStatus('No ad available. Try again later.');
              retryCount.current += 1;
              setStatus(AdStatus.ERROR);
              setIsLoading(false);
              startCooldownTimer();
            }
          });

        window.googletag.enableServices();
        loadAd();
      });
    };

    return () => {
      document.head.removeChild(script);
      if (rewardedSlotRef.current) {
        window.googletag.destroySlots([rewardedSlotRef.current]);
      }
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, [loadAd, startCooldownTimer]);

  useEffect(() => {
    if (
      cooldownTime === 0 &&
      isAutoRequestEnabled &&
      !isAdReady &&
      !isLoading
    ) {
      // console.log('loadAd');
      loadAd();
    }
  }, [cooldownTime, isAutoRequestEnabled, isAdReady, isLoading, loadAd]);

  const showAd = useCallback(() => {
    // window.googletag.pubads().refresh([rewardedSlotRef.current]);
    if (isAdReady && makeRewardedVisibleRef.current) {
      makeRewardedVisibleRef.current();
      setStatus(AdStatus.SHOWING);
    } else {
      setStatus(AdStatus.ERROR);
    }
  }, [isAdReady]);

  const requestNewAd = useCallback(() => {
    if (!isLoading && !isAdReady) {
      if (cooldownTime === 0) {
        loadAd();
      } else {
        // setStatus(
        //   `Please wait ${cooldownTime} seconds before requesting a new ad.`,
        // );
      }
    } else if (isAdReady) {
      // setStatus('An ad is already ready to show. You can display it now.');
      setStatus(AdStatus.READY);
    }
  }, [isLoading, isAdReady, cooldownTime, loadAd]);

  const toggleAutoRequest = useCallback(() => {
    setIsAutoRequestEnabled((prev) => !prev);
  }, []);

  const requestNewAdWithAuto = useCallback(() => {
    setIsAutoRequestEnabled(true);
  }, [setIsAutoRequestEnabled]);

  const stopAutoRequest = useCallback(() => {
    setIsAutoRequestEnabled(false);
    retryCount.current = 0;
  }, [setIsAutoRequestEnabled, retryCount]);

  return {
    status,
    isAdReady,
    isLoading,
    rewardPayload,
    showAd,
    requestNewAd,
    cooldownTime,
    isAutoRequestEnabled,
    toggleAutoRequest,
    requestNewAdWithAuto,
    stopAutoRequest,
    retryCount,
  };
};

export default useRewardedAd;
